import { Divider } from '@ps-design-system/components/Divider';
import { Form } from '@ps-design-system/components/Form';
import { InputField } from '@ps-design-system/components/Input';
import { Text } from '@ps-design-system/components/Text';
import { ToggleField } from '@ps-design-system/components/Toggle';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useCheckoutContext } from '../../CheckoutContextProvider';
import { getCurrencyFromListing } from '../../checkout-utils';
import { ActivityCombobox } from './ActivityCombobox';
import { AddOnsFormFields } from './AddOnsFormFields';
import { BookingScheduleInput } from './BookingScheduleInput';
import { HostMessageSection } from './HostMessageSection';
import { HostRulesSection } from './HostRulesSection/HostRulesSection';

export const BookingDetailsForm = ({ intl }) => {
  const {
    inventory,
    quote,
    bookingDetailsForm,
    isFetchingQuote,
    listing,
    isSubmittingRequest
  } = useCheckoutContext();

  const { masterContract } = quote || {};
  const currency = getCurrencyFromListing(listing);

  return (
    !!bookingDetailsForm && (
      <Form testId="booking-details-form" spacing={0} {...bookingDetailsForm}>
        <BookingScheduleInput
          disabled={isSubmittingRequest}
          form={bookingDetailsForm}
          spaceId={listing?.parentSpaceId}
          locale={intl?.locale}
          extendDayDisabled={isFetchingQuote || isSubmittingRequest}
        />

        <div style={{ marginTop: '24px' }}>
          <ActivityCombobox
            name="activity"
            fullWidth={true}
            disabled={isSubmittingRequest}
          />
        </div>

        <div className="booking-details-form__guests-alcohol">
          <div style={{ flex: 1 }}>
            <InputField
              testId="total-guests-input"
              name="totalGuests"
              fullWidth={true}
              label={intl.formatMessage({
                id: 'booking_details_form.total_guests_label',
                defaultMessage: 'How many people will attend?'
              })}
              placeholder={intl.formatMessage(
                {
                  id: 'booking_details_form.total_guests_placeholder',
                  defaultMessage: 'Max capacity is {capacity}'
                },
                {
                  capacity: listing?.number_guests
                }
              )}
              type="number"
              min={1}
              disabled={isSubmittingRequest}
            />
          </div>

          <div style={{ flex: 1 }}>
            <ToggleField
              testId="alcohol-consumed-toggle"
              name="alcoholConsumed"
              fullWidth={true}
              label={intl.formatMessage({
                id: 'booking_details_form.alcohol_consumed_label',
                defaultMessage: 'Will alcohol be consumed?'
              })}
              options={[
                {
                  label: intl.formatMessage({
                    id: 'booking_details_form.yes',
                    defaultMessage: 'Yes'
                  }),
                  value: true
                },
                {
                  label: intl.formatMessage({
                    id: 'booking_details_form.no',
                    defaultMessage: 'No'
                  }),
                  value: false
                }
              ]}
              disabled={isSubmittingRequest}
            />
          </div>
        </div>

        <Divider />

        <HostMessageSection
          hostAvatarUrl={listing?.ownerAvatarUrl}
          hostFirstName={listing?.ownerFirstName}
          intl={intl}
          disabled={isSubmittingRequest}
        />

        <div style={{ marginTop: '32px' }}>
          <HostRulesSection disabled={isSubmittingRequest} />
        </div>

        {!!inventory?.length && (
          <>
            <Divider />
            <div>
              <Text
                typeface="headline-3-b"
                element="h4"
                style={{ margin: 0, marginBottom: '24px' }}
              >
                <FormattedMessage
                  id="booking_details_form.select_add_ons"
                  defaultMessage="Select add-ons"
                />
              </Text>

              <AddOnsFormFields
                intl={intl}
                inventory={inventory}
                currency={currency}
                isFetchingQuote={isFetchingQuote}
                watchFormValues={bookingDetailsForm.watch}
                disabled={isSubmittingRequest}
                addOnsTotalCost={
                  masterContract?.calculations?.add_ons_guest_with_tax
                }
              />
            </div>
          </>
        )}
      </Form>
    )
  );
};

BookingDetailsForm.displayName = 'BookingDetailsForm';
BookingDetailsForm.propTypes = {
  intl: PropTypes.object
};
